import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { FaArrowLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import {
  createAdminUser,
  updateAdminUser,
  getAdminUserById,
  getRoles,
} from "../../../service/api";
import { decryptString } from "../../../config/validation";

const AdminUserForm = ({ onBack, editId = null }) => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [roleId, setRoleId] = useState("");
  const [isBlock, setIsBlock] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchRoles();
    if (editId) {
      fetchUser();
    }
  }, [editId]);

  const fetchRoles = async () => {
    try {
      const res = await getRoles({ pagination: "false" });
      if (res?.status && res?.data) {
        setRoles(res.data.result || []);
      }
    } catch (e) {
      console.error("Failed to fetch roles");
    }
  };

  const fetchUser = async () => {
    setFetchLoading(true);
    try {
      const res = await getAdminUserById(editId);
      if (res?.status && res?.data) {
        setFirstName(res.data.firstName || "");
        setLastName(res.data.lastName || "");
        setEmail(res.data.email || "");
        setPhoneNumber(res.data.phoneNumber || "");
        setRoleId(res.data.roleId || "");
        setIsBlock(res.data.isBlock || false);
      }
    } catch (e) {
      toast.error("Failed to fetch admin user details", { toastId: 'admin-user-form-fetch-error' });
    }
    setFetchLoading(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!editId) {
      if (!email.trim()) newErrors.email = "Email is required";
      if (!password.trim()) newErrors.password = "Password is required";
      else if (password.length < 8)
        newErrors.password = "Password must be at least 8 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      let res;
      if (editId) {
        const plainData = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber: phoneNumber.trim(),
          roleId: roleId || null,
          isBlock,
        };
        const encryptedData = await decryptString(plainData);
        res = await updateAdminUser(editId, { encrypt: encryptedData });
      } else {
        const plainData = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          password,
          phoneNumber: phoneNumber.trim(),
          roleId: roleId || null,
        };
        const encryptedData = await decryptString(plainData);
        res = await createAdminUser({ encrypt: encryptedData });
      }

      if (res?.status) {
        toast.success(
          res?.message || (editId ? "Admin user updated" : "Admin user created"),
          { toastId: 'admin-user-form-save-success' },
        );
        onBack();
      } else {
        toast.error(res?.message || "Operation failed", { toastId: 'admin-user-form-save-error' });
      }
    } catch (e) {
      toast.error(e?.data?.message || "Something went wrong", { toastId: 'admin-user-form-save-error' });
    }
    setLoading(false);
  };

  if (fetchLoading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="content container-fluid">
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3
              className="page-title"
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
            >
              <FaArrowLeft
                onClick={onBack}
                style={{ cursor: "pointer", fontSize: "18px" }}
              />
              {editId ? "Edit Admin User" : "Add Admin User"}
            </h3>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel
                      style={{ marginBottom: "8px", fontWeight: "500" }}
                    >
                      First Name *
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel
                      style={{ marginBottom: "8px", fontWeight: "500" }}
                    >
                      Last Name *
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel
                      style={{ marginBottom: "8px", fontWeight: "500" }}
                    >
                      Email *
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={!!editId}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel
                      style={{ marginBottom: "8px", fontWeight: "500" }}
                    >
                      Phone Number
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {!editId && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <InputLabel
                        style={{ marginBottom: "8px", fontWeight: "500" }}
                      >
                        Password *
                      </InputLabel>
                      <div style={{ position: "relative" }}>
                        <TextField
                          fullWidth
                          size="small"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          error={!!errors.password}
                          helperText={errors.password}
                        />
                        <span
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#666",
                          }}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel
                      style={{ marginBottom: "8px", fontWeight: "500" }}
                    >
                      Assign Role
                    </InputLabel>
                    <select
                      className="form-control"
                      value={roleId}
                      onChange={(e) => setRoleId(e.target.value)}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role._id} value={role._id}>
                          {role.name}
                          {role.roleType === 1 ? " (Super Admin)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {editId && (
                  <div className="col-md-6">
                    <div className="form-group">
                      <InputLabel
                        style={{ marginBottom: "8px", fontWeight: "500" }}
                      >
                        Status
                      </InputLabel>
                      <select
                        className="form-control"
                        value={isBlock ? "blocked" : "active"}
                        onChange={(e) =>
                          setIsBlock(e.target.value === "blocked")
                        }
                      >
                        <option value="active">Active</option>
                        <option value="blocked">Blocked</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: "24px" }}>
                <button
                  className="btn btn-primary me-2"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : editId ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </button>
                <button className="btn btn-secondary" onClick={onBack}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserForm;
